import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function DiagnosticTakePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const data = await api.diagnosticGetSession(sessionId);
      if (data.submitted_at) {
        navigate(`/diagnostic/results/${sessionId}`);
        return;
      }
      setSession(data);
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load diagnostic test');
      navigate('/diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    const totalQuestions = session.questions.length;
    const answeredCount = Object.keys(answers).filter(k => answers[k]?.trim()).length;
    
    if (answeredCount < totalQuestions) {
      if (!window.confirm(`You have answered ${answeredCount} out of ${totalQuestions} questions. Submit anyway?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.diagnosticSubmit(sessionId, answers);
      toast.success('Test submitted! Viewing results...');
      navigate(`/diagnostic/results/${sessionId}`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit test');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!session) return null;

  const groupedQuestions = {};
  session.questions.forEach(q => {
    if (!groupedQuestions[q.type]) {
      groupedQuestions[q.type] = [];
    }
    groupedQuestions[q.type].push(q);
  });

  const sections = Object.entries(groupedQuestions);
  const [currentType, currentQuestions] = sections[currentSection] || [];
  const totalAnswered = Object.keys(answers).filter(k => answers[k]?.trim()).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/diagnostic')}
            className="rounded-full"
            data-testid="diagnostic-take-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Exit Test
          </Button>
          <div className="text-sm text-gray-600">
            Progress: <span className="font-semibold text-emerald-600">{totalAnswered}/{session.questions.length}</span>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {sections.map(([type, qs], idx) => {
            const sectionAnswered = qs.filter(q => answers[q.id]?.trim()).length;
            const isComplete = sectionAnswered === qs.length;
            
            return (
              <Button
                key={type}
                variant={currentSection === idx ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentSection(idx)}
                className={`whitespace-nowrap rounded-full ${isComplete ? 'border-green-500' : ''}`}
                data-testid={`diagnostic-section-tab-${idx}`}
              >
                {qs[0].type_name}
                {isComplete && <CheckCircle2 className="w-4 h-4 ml-2 text-green-600" />}
              </Button>
            );
          })}
        </div>

        {/* Questions */}
        <Card data-testid="diagnostic-question-card">
          <CardHeader>
            <CardTitle className="text-emerald-900">{currentQuestions[0]?.type_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentQuestions.map((q, idx) => (
              <div key={q.id} className="p-4 border rounded-lg bg-white space-y-3">
                <div className="font-semibold text-gray-900">
                  Question {idx + 1}
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {q.question_text}
                </div>
                
                {q.options && q.options.length > 0 ? (
                  <div className="space-y-2">
                    {q.options.map((opt, optIdx) => {
                      const letter = opt.charAt(0);
                      return (
                        <label key={optIdx} className="flex items-start gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="radio"
                            name={q.id}
                            value={letter}
                            checked={answers[q.id] === letter}
                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                            className="mt-1"
                            data-testid={`diagnostic-answer-${q.id}-${letter}`}
                          />
                          <span className="text-sm text-gray-700">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <Textarea
                    placeholder="Type your answer here..."
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    rows={3}
                    data-testid={`diagnostic-answer-${q.id}`}
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="rounded-full"
            data-testid="diagnostic-prev-section-button"
          >
            Previous Section
          </Button>
          
          {currentSection === sections.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
              data-testid="diagnostic-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Test'
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
              className="rounded-full"
              data-testid="diagnostic-next-section-button"
            >
              Next Section
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
